import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { MainTableFilterModel } from 'src/app/shared/models/main-table-filter.model';
import { ActivityStatus } from 'src/app/shared/enums/activity-status.enum';
import { MainTableFilteringHelperService } from 'src/app/shared/main-table-module/services/main-table-filtering-helper.service';
import { createFilterParams } from 'src/app/shared/functions/create-filter-params';
import { environment } from '../../../environments/environment';
import {
  IApiResponseGroupTasks,
  ITask,
  ITaskRequest,
  ITaskPerformRequest,
  ITaskPostpone,
  ITaskPerformResponse,
} from '../models/task.model';
import { getDepartmentId, getTenantId } from '../../services/store-snapshot.service';
import { IdName } from '../../shared/models/id-name';
import { TaskLog } from '../models/task-log.model';
import { TaskOverview } from '../models/task-overview.model';
import { FormDataService } from '../../shared/attachments/service/form-data.service';

const COLUMN_TO_KEY_MAP: Record<string, string> = {
  expiry: 'ExpiryDate',
  accessPositions: 'PositionsWithAccess.Name',
  accessPositionGroups: 'PositionGroupsWithAccess.Name',
  accessRoles: 'RolesWithAccess.Name',
  lastPerformed: 'LastWorkDate',
  pausable: 'Pausable',
  responsiblePositions: 'ResponsibleForPositions.Name',
  responsiblePositionGroups: 'ResponsibleForPositionGroups.Name',
  responsibleRoles: 'ResponsibleForRoles.Name',
  responsibleUsers: 'ResponsibleForUsers.Name',
};
const OR_GROUP_MAP: Record<string, string> = {
  accessPositions: 'access',
  accessPositionGroups: 'access',
  accessRoles: 'access',
  responsiblePositions: 'responsible',
  responsiblePositionGroups: 'responsible',
  responsibleRoles: 'responsible',
  responsibleUsers: 'responsible',
};
@Injectable({ providedIn: 'root' })
export class TaskApiService {
  private get url(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/${getDepartmentId()}/Works`;
  }

  private get tasksForIOUrl(): string {
    return `${this.url}/Interconnected`;
  }

  constructor(private http: HttpClient) {}

  loadTasks(currentFilter?: MainTableFilterModel): Observable<IApiResponseGroupTasks> {
    const url = `${this.url}/menu/paged`;
    let params = createFilterParams(currentFilter);

    if (currentFilter?.additional?.menuId) {
      params = params.set('menuId', currentFilter.additional?.menuId);
    }
    if (currentFilter?.additional?.activityStatus === ActivityStatus.Deleted) {
      params = params.set('IsDeleted', 'true');
    }
    if (currentFilter?.additional?.activityStatus === ActivityStatus.Deactivated) {
      params = params.set('IsDisabled', 'true');
    }
    if (currentFilter?.additional?.activityStatus === ActivityStatus.Completed) {
      params = params.set('IsCompleted', 'true');
    }

    params = MainTableFilteringHelperService.appendFilteringParams(
      params,
      currentFilter,
      COLUMN_TO_KEY_MAP,
      OR_GROUP_MAP
    );

    return this.http.get<IApiResponseGroupTasks>(url, { params });
  }

  loadTaskById(TaskId: string): Observable<ITask> {
    return this.http.get<ITask>(`${this.url}/${TaskId}`);
  }

  loadTaskVersionById(TaskId: string, TaskVersionId: string): Observable<ITaskPerformResponse> {
    return this.http.get<ITaskPerformResponse>(`${this.url}/${TaskId}/log/${TaskVersionId}`);
  }

  loadTasksForIO(): Observable<IdName[]> {
    return this.http.get<IdName[]>(this.tasksForIOUrl);
  }

  loadTaskLog(TaskId: string): Observable<TaskLog> {
    return this.http.get<TaskLog>(`${this.url}/${TaskId}/log`);
  }

  loadTasksByMenuId(menuId: string): Observable<TaskOverview[]> {
    let url;
    if (menuId) {
      url = `${this.url}/menu?menuId=${menuId}`;
    } else {
      url = `${this.url}/menu`;
    }
    return this.http.get<TaskOverview[]>(url);
  }

  createTask(task: ITaskRequest): Observable<IdName> {
    const formData = FormDataService.mapToFormData(task);
    return this.http.post<IdName>(this.url, formData);
  }

  updateTask(task: ITaskRequest): Observable<void> {
    const formData = FormDataService.mapToFormData(task);
    return this.http.put<void>(`${this.url}/${task.id}`, formData);
  }

  deleteTask(taskId: string): Observable<void> {
    return this.http.delete<void>(`${this.url}/${taskId}`);
  }

  performTask(task: ITaskPerformRequest): Observable<void> {
    const formData = FormDataService.mapToFormData(task);
    const url = `${this.url}/${task.id}/perform/v2`;
    return this.http.put<void>(url, formData);
  }

  postponeTask({ id, newExpiryDate, comment }: ITaskPostpone): Observable<void> {
    return this.http.put<void>(`${this.url}/${id}/postpone`, { newExpiryDate, comment });
  }

  restoreTask(id: string): Observable<void> {
    return this.http.put<void>(`${this.url}/${id}/restore`, {});
  }

  loadTaskDraftPerform(taskId: string): Observable<ITaskPerformResponse> {
    return this.http.get<ITaskPerformResponse>(`${this.url}/${taskId}/draftPerform`);
  }

  saveTaskDraftPerform(task: ITaskPerformRequest): Observable<void> {
    const formData = FormDataService.mapToFormData(task);
    return this.http.put<void>(`${this.url}/${task.id}/draftPerform`, formData);
  }

  printExcel(id: string): Observable<Blob> {
    const url = `${this.url}/${id}/Excel`;
    return this.http.get(url, { responseType: 'blob' });
  }

  deactivateTask(id: string): Observable<void> {
    const url = `${this.url}/${id}/disable`;
    return this.http.put<void>(url, { id });
  }

  reactivateTask(id: string): Observable<void> {
    const url = `${this.url}/${id}/enable`;
    return this.http.put<void>(url, { id });
  }

  completeTask(id: string): Observable<void> {
    const url = `${this.url}/${id}/complete`;
    return this.http.patch<void>(url, { id });
  }
}
